/**
 * Research — /app/research (Faraz 2026-09-06: "We need a research page in
 * the procurement surface just to show this table. I want to see the
 * output and do the order planning later."). Read-only view over
 * GET /api/order-planning/runs/:runId/research — every candidate the
 * nightly run priced, whether or not it made a plan. No buy actions here;
 * order planning (PlanningPage) owns the actual plan.
 *
 * Rebuilt 2026-09-11 per the research-verdict-simplification design (Faraz:
 * "We made research way complicated ... We have a list. We find with
 * compare with Amazon and Keepa and find the products." — see
 * docs/plans/2026-09-11-research-verdict-simplification-design.md). The
 * page's job is picking items to buy: three tabs (Buy/Fix first/Everything)
 * plus search replace the old nine independent filter selects and tier
 * chips; full per-row detail moved into the side panel (ResearchRowPanel).
 */
import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  CreateOrderPlanResponse,
  ListOrderPlansResponse,
  researchSearchAsin,
  type CreateOrderPlanLineRequest,
  type PlanningProviderProgress,
  type PlanningResearchRow,
  type PlanningResearchRunState,
  type PlanningVelocityTier,
  type PlanningVerdictReason,
} from '@sourcito/schemas';
import { AppShell } from '../../components/AppShell';
import { ProductLink } from '../../components/ProductLink';
import { PlanningDiscoverySection } from './PlanningPage';
import { ProductThumb } from '../../components/ProductThumb';
import { SkeletonRows } from '../../components/Skeleton';
import {
  Badge,
  Button,
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../design';
import {
  FIX_REASON_CODES,
  approvalNeededSubject,
  isLowestFbaOfferAnchor,
  money,
  moneyWhole,
  packBadge,
  percent,
  reasonFilterOptionLabel,
  researchHoldSentence,
  researchStatus,
  velocityChip,
  verdictBucketLabel,
  verdictCountsLine,
  type PlanningResearchFilters,
  useLatestPlanningRun,
  useLatestCompletePlanningRun,
  usePlanningResearch,
  useRequestGatingCheck,
  createOrderPlanLine,
} from '../../lib/order-planning';
import { apiFetch } from '../../lib/api';
import { shortDateLabel } from '../../lib/shipments';
import { ResearchRowPanel, researchDecisionReason } from './ResearchRowPanel';

const PAGE_SIZE = 100;

type SortKey = NonNullable<PlanningResearchFilters['sort']>;
type SortDir = NonNullable<PlanningResearchFilters['dir']>;

/** The three tabs — 'all' ("Everything") sends no `verdict` param, every
 *  researched row regardless of bucket. Default is 'buy': the page's job is
 *  picking items to buy, so that's what Faraz should land on. */
type VerdictTab = 'buy' | 'fix' | 'all';

const TABS: { key: VerdictTab; label: string }[] = [
  { key: 'buy', label: verdictBucketLabel('buy') },
  { key: 'fix', label: verdictBucketLabel('fix') },
  { key: 'all', label: 'Everything' },
];

/** "Split the list" (Faraz 2026-09-26; three-way split 2026-09-28) — the Buy
 *  tab's own sub-filter over Amazon sellability, distinct from the verdict
 *  bucket (all three groups stay verdict 'buy'; see docs/plans for the
 *  design). 'now' sends gating=open, 'afterApproval' sends
 *  gating=approval_required, 'unchecked' sends gating=unchecked — "not
 *  checked yet" is now its own group instead of being folded into "after
 *  approval", since gating carried across nightly runs (2026-09-28 tenant-
 *  level ASIN cache design) means it's no longer rare. */
type BuySplitOption = 'now' | 'afterApproval' | 'unchecked';

const BUY_SPLIT_OPTIONS: { key: BuySplitOption; label: string }[] = [
  { key: 'now', label: 'Buy now' },
  { key: 'afterApproval', label: 'After approval' },
  { key: 'unchecked', label: 'Not checked yet' },
];

/** Keep the status copy honest: Amazon is the baseline market check, while
 * Keepa is shown only when this row carries one of its enrichment fields. */
export function researchSourceLabel(row: PlanningResearchRow): string {
  const hasAmazon = row.anchorPriceCents !== null || row.estFeesCents !== null;
  const hasKeepa = row.priceContext.bbAvg90Cents !== null || row.velocity.source === 'rank_drops';
  if (hasAmazon && hasKeepa) return 'Amazon + Keepa';
  if (hasKeepa) return 'Keepa';
  if (hasAmazon) return 'Amazon';
  return 'Not checked';
}

// 2026-09-17 provider-queues Task 6: `researchStatus` moved to
// lib/order-planning.ts so ResearchRowPanel.tsx can reuse it without a
// circular import (ResearchPage.tsx already imports FROM
// ResearchRowPanel.tsx for researchDecisionReason) — re-exported here (the
// import above) so this stays the one stable import path this page's own
// tests already use.
export { researchStatus };

/** Nightly research is expected to be useful for roughly one day. Keep the
 * threshold here simple and visible; no per-source freshness policy yet. */
export function researchFreshness(
  checkedAt: string | null,
  now = Date.now()
): 'fresh' | 'stale' | 'unknown' {
  if (!checkedAt) return 'unknown';
  const checked = Date.parse(checkedAt);
  if (!Number.isFinite(checked)) return 'unknown';
  return now - checked > 36 * 60 * 60 * 1000 ? 'stale' : 'fresh';
}

function runStatusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

// 2026-09-17 provider-queues Task 6 ("Research UI and observability" —
// docs/plans/2026-09-17-research-provider-queues-design.md). The design's
// five display states, distinct from both `run.status` (RunStrip's own
// badge, above) and the row-level Buy/Fix first/No business verdict.
const RUN_STATE_LABEL: Record<PlanningResearchRunState, string> = {
  preparing: 'Preparing',
  researching: 'Researching',
  enriching: 'Enriching',
  complete: 'Complete',
  complete_with_gaps: 'Complete with gaps',
};

const RUN_STATE_BADGE_VARIANT: Record<
  PlanningResearchRunState,
  'neutral' | 'positive' | 'warning' | 'info'
> = {
  preparing: 'neutral',
  researching: 'info',
  enriching: 'info',
  complete: 'positive',
  complete_with_gaps: 'warning',
};

/** All user-facing times are Central, never UTC (Faraz: report times in
 *  Central) — this page has no prior Central-time formatter to reuse, so
 *  one lives here. Returns null for a missing/unparseable timestamp so
 *  callers can render an em dash or omit the line entirely. */
export function formatCentralTime(iso: string | null): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(parsed);
}

/** Compact provider progress summary (design doc: "Keep the current
 *  paginated Research table and decision fields. Add one compact progress
 *  summary"). Absent entirely for a response with no `providerProgress`
 *  (a legacy execution-version-1 run, or a payload captured before this
 *  shipped) — additive, never required. Provider counters are kept visibly
 *  separate from the Buy/Fix first/No business verdict counts in RunStrip. */
function ProviderProgressSummary({
  progress,
  resultsUpdatedAt,
}: {
  progress: PlanningProviderProgress;
  resultsUpdatedAt: string | null;
}) {
  const updated = formatCentralTime(resultsUpdatedAt);
  const nextRetry = formatCentralTime(progress.nextEligibleRetryAt);
  return (
    <div
      role="status"
      className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg border border-border-subtle bg-surface px-4 py-3 text-xs text-fg-secondary"
    >
      <Badge variant={RUN_STATE_BADGE_VARIANT[progress.runState]}>
        {RUN_STATE_LABEL[progress.runState]}
      </Badge>
      <span className="tabular-nums">
        Amazon {progress.amazon.completed}/{progress.amazon.total} · {progress.amazon.active} active
        · {progress.amazon.waiting} waiting · {progress.amazon.failed} failed
      </span>
      <span className="tabular-nums">
        Keepa {progress.keepa.completed}/{progress.keepa.total} · {progress.keepa.reused} reused ·{' '}
        {progress.keepa.waitingForTokens} waiting for tokens · {progress.keepa.unavailableOrFailed}{' '}
        unavailable/failed
      </span>
      {updated && <span>Results updated {updated}</span>}
      {nextRetry && <span>Next retry {nextRetry}</span>}
    </div>
  );
}

export function ResearchPage() {
  const runQuery = useLatestPlanningRun();
  // Last-complete fallback (Faraz 2026-09-28): while the newest run is only
  // partly researched, showing it hides the useful finished results from
  // the previous complete run. Only fetched when it's actually needed —
  // the newest run exists and isn't complete yet.
  const newestNotComplete = runQuery.data != null && runQuery.data.status !== 'complete';
  const completeRunQuery = useLatestCompletePlanningRun(newestNotComplete);
  // True once there's an older complete run to fall back to. Component
  // state only — resets on reload, per the design.
  const hasCompleteFallback = newestNotComplete && completeRunQuery.data != null;
  const [viewingInProgress, setViewingInProgress] = useState(false);

  const [tab, setTab] = useState<VerdictTab>('buy');
  // "Split the list" (Faraz 2026-09-26) — the Buy tab's own sub-filter,
  // separate from `tab` so it can reset independently. Default 'now': the
  // sellable-today items are what Faraz should land on first.
  const [buySplit, setBuySplit] = useState<BuySplitOption>('now');
  const [reasonFilter, setReasonFilter] = useState<PlanningVerdictReason | 'all'>('all');
  const [velocityFilter, setVelocityFilter] = useState<PlanningVelocityTier | 'all'>('all');
  // Pack filter, re-exposed 2026-09-22. The route, reader and counts never
  // stopped supporting it; only this control was lost in the three-tab
  // consolidation, which left 188 pack-suspect rows on the 2026-09-26 run
  // findable only by scrolling until you spotted the badge.
  type PackFilter = NonNullable<PlanningResearchFilters['pack']> | 'all';
  const [packFilter, setPackFilter] = useState<PackFilter>('all');
  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Default sort is the Buy tab's "Profit/mo" (margin x sales, Faraz
  // 2026-09-27: "top selling top margin products on top") since 'tab'
  // starts on 'buy' above; the effect below keeps this in sync on every
  // tab switch, and a header click (sortByColumn) overrides it until the
  // next switch.
  const [sort, setSort] = useState<SortKey>('profit');
  const [dir, setDir] = useState<SortDir>('desc');
  const [offset, setOffset] = useState(0);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [lastViewedRowId, setLastViewedRowId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(() => new Set());
  const [addingSelected, setAddingSelected] = useState(false);
  const [addFeedback, setAddFeedback] = useState<string | null>(null);

  // The run the page actually displays: the last complete run when the
  // newest one isn't done yet (unless the user chose to view the
  // in-progress run), otherwise the newest run as always.
  const displayedRunSummary =
    hasCompleteFallback && !viewingInProgress ? completeRunQuery.data : runQuery.data;

  // Header-click sorting only (the old Sort select and Asc/Desc toggle
  // button are gone) — clicking a column that isn't already active starts
  // it at 'desc'; clicking the ALREADY active column toggles direction.
  function sortByColumn(key: SortKey) {
    if (key === sort) {
      setDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSort(key);
      setDir('desc');
    }
  }

  // Debounced search (300ms).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(rawQuery), 300);
    return () => clearTimeout(handle);
  }, [rawQuery]);

  // A changed tab/reason/search/sort re-starts pagination at the top.
  useEffect(() => {
    setOffset(0);
  }, [
    tab,
    buySplit,
    reasonFilter,
    velocityFilter,
    packFilter,
    debouncedQuery,
    sort,
    dir,
    displayedRunSummary?.id,
  ]);

  // Switching off the Fix-first tab drops any reason narrowing — the Reason
  // select only exists there, so a stale choice must not silently keep
  // filtering the other tabs.
  useEffect(() => {
    if (tab !== 'fix') setReasonFilter('all');
  }, [tab]);

  // Switching back to the Buy tab resets the split to "Buy now" (Faraz
  // 2026-09-26) — the control only exists there, so a stale "After approval"
  // choice must not silently keep filtering once you leave and come back.
  useEffect(() => {
    if (tab === 'buy') setBuySplit('now');
  }, [tab]);

  // Each tab has its own default sort (Faraz 2026-09-27): Buy leads with
  // combined Profit/mo (margin x sales); Fix first/Everything keep the
  // pre-existing margin-desc default. Switching tabs resets to that tab's
  // default even if a header click had overridden it — a header click after
  // landing on a tab still overrides until the next switch.
  useEffect(() => {
    if (tab === 'buy') {
      setSort('profit');
      setDir('desc');
    } else {
      setSort('margin');
      setDir('desc');
    }
  }, [tab]);

  const searchAsin = researchSearchAsin(debouncedQuery);
  const activeTab = searchAsin ? 'all' : tab;
  const filters = useMemo<PlanningResearchFilters>(
    () => ({
      verdict: activeTab === 'all' ? undefined : activeTab,
      reason: !searchAsin && tab === 'fix' && reasonFilter !== 'all' ? reasonFilter : undefined,
      // The Buy tab's split owns `gating` while active — "Buy now" sends
      // gating=open, "After approval" sends gating=approval_required (real
      // gated verdicts only, since 2026-09-28 — see BuySplitOption's doc),
      // "Not checked yet" sends gating=unchecked. No other tab sets
      // `gating`, so there is nothing for this to fight with.
      gating:
        !searchAsin && activeTab === 'buy'
          ? buySplit === 'now'
            ? ['open']
            : buySplit === 'afterApproval'
              ? ['approval_required']
              : ['unchecked']
          : undefined,
      velocity: searchAsin || velocityFilter === 'all' ? undefined : [velocityFilter],
      pack: searchAsin || packFilter === 'all' ? undefined : packFilter,
      q: searchAsin ?? (debouncedQuery.trim() || undefined),
      sort,
      dir,
      limit: PAGE_SIZE,
      offset,
    }),
    [
      activeTab,
      searchAsin,
      tab,
      buySplit,
      reasonFilter,
      velocityFilter,
      packFilter,
      debouncedQuery,
      sort,
      dir,
      offset,
    ]
  );

  const runId = displayedRunSummary?.id ?? null;
  const researchQuery = usePlanningResearch(runId, filters, runId != null);
  const gatingCheck = useRequestGatingCheck();
  const research = researchQuery.data;
  const rows = research?.rows ?? [];
  const verdictCounts = research?.counts.verdict ?? { buy: 0, fix: 0, no: 0 };
  const buySplitCounts = research?.counts.buySplit ?? { now: 0, afterApproval: 0, unchecked: 0 };
  const reasonCounts = research?.counts.reason ?? {};
  // Whole-run pack totals (not filtered), same scope as reasonCounts — shown
  // in the pack select so the size of the queue is visible before choosing it.
  const packCounts = research?.counts.pack;
  const researchedCount = research?.counts.researched ?? 0;
  const incompleteCount = research?.counts.incomplete ?? 0;

  const selectedRow = rows.find((r) => r.candidateId === selectedRowId) ?? null;

  const displayRun =
    research?.run ??
    (displayedRunSummary
      ? { day: displayedRunSummary.day, status: displayedRunSummary.status, holdReason: null }
      : null);

  // Reason column only earns its place once a row can carry something other
  // than "Ready to buy" — the Buy tab omits it entirely.
  const showReasonColumn = activeTab !== 'buy';

  function toggleRow(rowId: string) {
    setSelectedRows((current) => {
      const next = new Set(current);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }

  async function addSelectedToBuyList() {
    const selected = rows.filter((row) => selectedRows.has(row.candidateId));
    if (selected.length === 0 || addingSelected) return;
    setAddingSelected(true);
    setAddFeedback(null);
    try {
      const plans = await apiFetch('/api/order-planning/plans?status=draft', {
        responseSchema: ListOrderPlansResponse,
      });
      const plan =
        plans[0] ??
        (await apiFetch('/api/order-planning/plans', {
          method: 'POST',
          body: JSON.stringify({}),
          responseSchema: CreateOrderPlanResponse,
        }));
      for (const row of selected) {
        const input: CreateOrderPlanLineRequest = {
          researchCandidateId: row.candidateId,
          source: row.source,
          asin: row.asin ?? undefined,
          upc: row.upc ?? undefined,
          itemNo: row.itemNo ?? undefined,
          description: row.description ?? row.itemNo ?? row.asin ?? row.upc ?? row.key,
          qtyPlanned: row.proposalQuantity && row.proposalQuantity > 0 ? row.proposalQuantity : 1,
          expectedUnitCostCents: row.costCents ?? undefined,
          intendedPriceCents:
            row.anchorPriceCents && row.anchorPriceCents > 0 ? row.anchorPriceCents : undefined,
        };
        await createOrderPlanLine(plan.id, input);
      }
      setSelectedRows(new Set());
      setAddFeedback(
        `Added ${selected.length} item${selected.length === 1 ? '' : 's'} to the buy list.`
      );
    } catch (error) {
      setAddFeedback(error instanceof Error ? error.message : 'Could not add selected items.');
    } finally {
      setAddingSelected(false);
    }
  }

  return (
    <AppShell>
      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
        <header>
          <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">OPS</p>
          <h1 className="font-display text-4xl sm:text-[44px] font-medium leading-[1.08] tracking-tight mt-3">
            Research.
          </h1>
          <p className="text-sm text-fg-secondary mt-3">
            Every product the nightly run priced, whether or not it made the plan.
          </p>
        </header>

        {runQuery.isLoading && (
          <p className="mt-8 text-sm text-fg-secondary">Loading the latest run…</p>
        )}
        {runQuery.isError && (
          <p role="alert" className="mt-8 text-sm text-negative">
            Could not load the latest buying run.
          </p>
        )}
        {!runQuery.isLoading && !runQuery.isError && !runQuery.data && (
          <div className="mt-8 bg-surface border border-border-subtle rounded-xl px-5 py-6">
            <p className="text-sm text-fg-secondary">
              No research yet. The nightly run has not completed for this account.
            </p>
          </div>
        )}

        <details
          className="mt-5 rounded-lg border border-border-subtle p-4"
          onToggle={(e) => setToolsOpen(e.currentTarget.open)}
        >
          <summary className="cursor-pointer text-sm font-medium">
            Research tools · Runs, buying settings & reorder suggestions
          </summary>
          {toolsOpen && <PlanningDiscoverySection />}
        </details>
        {runId && displayRun && (
          <>
            {hasCompleteFallback && (
              <div
                role="status"
                className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border-subtle bg-surface px-4 py-3 text-sm"
              >
                <p className="text-fg-secondary">
                  {viewingInProgress
                    ? 'Viewing the in-progress run.'
                    : `A newer run (${shortDateLabel(runQuery.data!.day)}) is in progress. You're viewing the last complete run.`}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setViewingInProgress((v) => !v)}
                >
                  {viewingInProgress ? 'Back to last complete run' : 'View in-progress run'}
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <RunStrip run={displayRun} verdictCounts={verdictCounts} />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={gatingCheck.isPending}
                  onClick={() => gatingCheck.mutate({ runId, recheck: true })}
                >
                  {gatingCheck.isPending ? 'Checking…' : 'Check sellability'}
                </Button>
              </div>
            </div>
            {gatingCheck.isSuccess && (
              <p className="mt-2 text-sm text-fg-secondary">
                Sellability check queued. Results appear as rows are checked.
              </p>
            )}
            {gatingCheck.isError && (
              <p className="mt-2 text-sm text-negative">Could not queue the sellability check.</p>
            )}

            {research?.providerProgress && (
              <ProviderProgressSummary
                progress={research.providerProgress}
                resultsUpdatedAt={research.freshness?.lastMaterializedAt ?? null}
              />
            )}

            {(research?.freshness?.status === 'preparing' ||
              research?.freshness?.status === 'refreshing' ||
              researchQuery.isError) && (
              <div
                role="status"
                className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border-subtle bg-surface px-4 py-3 text-sm"
              >
                <p className="text-fg-secondary">
                  {researchQuery.isError
                    ? 'Research results could not be loaded.'
                    : research?.freshness?.status === 'preparing'
                      ? 'Preparing saved research results. This does not start new Amazon or Keepa research.'
                      : research?.freshness?.evidenceRefreshPending
                        ? 'Evidence updates are being processed. Showing the last saved results.'
                        : 'Refreshing saved decisions. Showing the last saved results.'}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={researchQuery.isFetching}
                  onClick={() => void researchQuery.refetch()}
                >
                  Refresh results
                </Button>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-end justify-between gap-3">
              <div
                role="tablist"
                aria-label="Verdict"
                className="flex gap-1 border-b border-border-subtle"
              >
                {TABS.map((t) => {
                  const count =
                    t.key === 'buy'
                      ? verdictCounts.buy
                      : t.key === 'fix'
                        ? verdictCounts.fix
                        : researchedCount + incompleteCount;
                  const isActive = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setTab(t.key)}
                      className={cn(
                        'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors duration-fast',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded-t-xs',
                        isActive
                          ? 'border-accent-primary text-fg-primary'
                          : 'border-transparent text-fg-secondary hover:text-fg-primary'
                      )}
                    >
                      {t.label} · {count}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {activeTab === 'fix' && (
                  <Select
                    value={reasonFilter}
                    onValueChange={(v) => setReasonFilter(v as PlanningVerdictReason | 'all')}
                  >
                    <SelectTrigger className="w-52" aria-label="Reason">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All reasons ({verdictCounts.fix})</SelectItem>
                      {FIX_REASON_CODES.map((code) => (
                        <SelectItem key={code} value={code}>
                          {reasonFilterOptionLabel(code, reasonCounts)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Select
                  value={searchAsin ? 'all' : velocityFilter}
                  disabled={!!searchAsin}
                  onValueChange={(v) => setVelocityFilter(v as PlanningVelocityTier | 'all')}
                >
                  <SelectTrigger className="w-40" aria-label="Sales velocity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sales rates</SelectItem>
                    <SelectItem value="pass">High sales</SelectItem>
                    <SelectItem value="marginal">Moderate sales</SelectItem>
                    <SelectItem value="fail">Low sales</SelectItem>
                    <SelectItem value="unknown">Unknown sales</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={searchAsin ? 'all' : packFilter}
                  disabled={!!searchAsin}
                  onValueChange={(v) => setPackFilter(v as PackFilter)}
                >
                  <SelectTrigger className="w-44" aria-label="Pack size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All pack sizes</SelectItem>
                    {/* 'suspect' first: it is the actionable one — the cost
                        basis is not credible and the row panel's pack
                        override is the fix. */}
                    <SelectItem value="suspect">
                      Verify pack{packCounts ? ` (${packCounts.suspect})` : ''}
                    </SelectItem>
                    <SelectItem value="unverified">
                      Pack unverified{packCounts ? ` (${packCounts.unverified})` : ''}
                    </SelectItem>
                    <SelectItem value="unclear">
                      Pack unclear{packCounts ? ` (${packCounts.unclear})` : ''}
                    </SelectItem>
                    <SelectItem value="known">
                      Pack known{packCounts ? ` (${packCounts.known})` : ''}
                    </SelectItem>
                  </SelectContent>
                </Select>

                <div className="relative max-w-sm flex-1 min-w-[220px]">
                  <Search
                    className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    aria-hidden="true"
                  />
                  <Input
                    inputSize="sm"
                    aria-label="Search research"
                    value={rawQuery}
                    onChange={(e) => setRawQuery(e.target.value)}
                    placeholder="Search product or ASIN…"
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            {activeTab === 'buy' && (
              <div role="tablist" aria-label="Buy split" className="mt-3 flex gap-1">
                {BUY_SPLIT_OPTIONS.filter(
                  (opt) => opt.key !== 'unchecked' || buySplitCounts.unchecked > 0
                ).map((opt) => {
                  const isActive = buySplit === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setBuySplit(opt.key)}
                      className={cn(
                        'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors duration-fast',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30',
                        isActive
                          ? 'border-accent-primary bg-accent/10 text-fg-primary'
                          : 'border-border-subtle text-fg-secondary hover:text-fg-primary'
                      )}
                    >
                      {opt.label} · {buySplitCounts[opt.key]}
                    </button>
                  );
                })}
              </div>
            )}

            {searchAsin && (
              <p className="mt-3 text-sm text-fg-secondary">
                ASIN lookup · All saved Research results in this run. Filters are temporarily
                ignored.
              </p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                disabled={selectedRows.size === 0 || addingSelected}
                loading={addingSelected}
                onClick={addSelectedToBuyList}
              >
                Add selected to buy list{selectedRows.size > 0 ? ` (${selectedRows.size})` : ''}
              </Button>
              {addFeedback && (
                <p role="status" className="text-sm text-fg-secondary">
                  {addFeedback}
                </p>
              )}
            </div>

            <ResearchTable
              emptyMessage={
                research?.freshness?.status === 'preparing'
                  ? 'Saved results will appear after preparation finishes.'
                  : research?.freshness?.status === 'refreshing'
                    ? 'No matching saved rows yet; evidence updates are still pending.'
                    : undefined
              }
              isLoading={researchQuery.isLoading}
              isError={researchQuery.isError}
              rows={rows}
              sort={sort}
              dir={dir}
              onSortClick={sortByColumn}
              showReasonColumn={showReasonColumn}
              onRowClick={(row) => {
                setLastViewedRowId(row.candidateId);
                setSelectedRowId(row.candidateId);
              }}
              lastViewedRowId={lastViewedRowId}
              onLinkVisit={setLastViewedRowId}
              selectedRows={selectedRows}
              onToggleRow={toggleRow}
            />

            {research && research.total > 0 && (
              <Pagination
                offset={offset}
                limit={PAGE_SIZE}
                total={research.total}
                onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                onNext={() => setOffset((o) => o + PAGE_SIZE)}
              />
            )}
          </>
        )}

        {selectedRow && (
          <ResearchRowPanel row={selectedRow} onClose={() => setSelectedRowId(null)} />
        )}
      </main>
    </AppShell>
  );
}

function RunStrip({
  run,
  verdictCounts,
}: {
  run: { day: string; status: string; holdReason: string | null };
  verdictCounts: { buy: number; fix: number; no: number };
}) {
  const sentence = researchHoldSentence(run.holdReason);
  return (
    <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
      <span className="text-fg-secondary">{shortDateLabel(run.day)}</span>
      <Badge
        variant={
          run.status === 'complete' ? 'positive' : run.status === 'failed' ? 'negative' : 'warning'
        }
      >
        {runStatusLabel(run.status)}
      </Badge>
      {sentence && <p className="text-fg-secondary">{sentence}</p>}
      <p className="text-fg-secondary tabular-nums">{verdictCountsLine(verdictCounts)}</p>
    </div>
  );
}

/** One clickable, sortable column header — clicking a header that isn't
 *  already active starts it at 'desc'; clicking the active header toggles
 *  direction. The active column shows a ▲/▼ arrow and the matching
 *  `aria-sort` value; every other sortable header is `aria-sort="none"`. */
function SortableHeader({
  label,
  sortKey,
  activeSort,
  dir,
  onSortClick,
  className,
}: {
  label: string;
  sortKey: SortKey;
  activeSort: SortKey;
  dir: SortDir;
  onSortClick: (key: SortKey) => void;
  className?: string;
}) {
  const isActive = activeSort === sortKey;
  return (
    <th
      className={cn('px-4 py-2 font-medium', className)}
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSortClick(sortKey)}
        className="inline-flex items-center gap-1 hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded-xs"
      >
        {label}
        {isActive && <span aria-hidden="true">{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

function ResearchTable({
  emptyMessage = 'No rows match these filters.',
  isLoading,
  isError,
  rows,
  sort,
  dir,
  onSortClick,
  showReasonColumn,
  onRowClick,
  lastViewedRowId,
  onLinkVisit,
  selectedRows,
  onToggleRow,
}: {
  emptyMessage?: string;
  isLoading: boolean;
  isError: boolean;
  rows: PlanningResearchRow[];
  sort: SortKey;
  dir: SortDir;
  onSortClick: (key: SortKey) => void;
  showReasonColumn: boolean;
  onRowClick: (row: PlanningResearchRow) => void;
  lastViewedRowId: string | null;
  onLinkVisit: (rowId: string) => void;
  selectedRows: Set<string>;
  onToggleRow: (rowId: string) => void;
}) {
  const colWidths = showReasonColumn
    ? ['w-64', 'w-20', 'w-24', 'w-20', 'w-24', 'w-20', 'w-24', 'w-40']
    : ['w-64', 'w-20', 'w-24', 'w-20', 'w-24', 'w-20', 'w-24'];

  return (
    // Sticky header stays pinned to this box's own scroll, not the page's
    // (Faraz 2026-09-11: "column names should be fixed on top row after
    // scrolling the page") — see the equivalent comment the old page carried.
    <div className="mt-3 bg-surface border border-border-subtle rounded-xl overflow-x-auto overflow-y-auto max-h-[calc(100dvh-5rem)]">
      {isLoading && (
        <table className="w-full min-w-[900px] text-sm table-fixed">
          <tbody className="divide-y divide-border-subtle">
            <SkeletonRows rows={6} colWidths={colWidths} cellClassName="px-4 py-2.5 align-top" />
          </tbody>
        </table>
      )}
      {isError && <p className="text-sm text-negative px-5 py-6">Could not load research.</p>}
      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-sm text-fg-secondary px-5 py-6">{emptyMessage}</p>
      )}
      {rows.length > 0 && (
        <table className="w-full min-w-[900px] text-sm table-fixed">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.14em] text-fg-tertiary text-left sticky top-0 z-20 bg-surface">
              <th className="w-12 px-3 py-2">
                <span className="sr-only">Select</span>
              </th>
              <SortableHeader
                className="w-64"
                label="Product"
                sortKey="title"
                activeSort={sort}
                dir={dir}
                onSortClick={onSortClick}
              />
              <SortableHeader
                className="text-right w-20"
                label="Cost"
                sortKey="cost"
                activeSort={sort}
                dir={dir}
                onSortClick={onSortClick}
              />
              <SortableHeader
                className="text-right w-24"
                label="Buy Box"
                sortKey="price"
                activeSort={sort}
                dir={dir}
                onSortClick={onSortClick}
              />
              <SortableHeader
                className="text-right w-20"
                label="Fees"
                sortKey="fees"
                activeSort={sort}
                dir={dir}
                onSortClick={onSortClick}
              />
              <SortableHeader
                className="text-right w-24"
                label="Margin"
                sortKey="margin"
                activeSort={sort}
                dir={dir}
                onSortClick={onSortClick}
              />
              <SortableHeader
                className="text-right w-20"
                label="Sales/mo"
                sortKey="sales"
                activeSort={sort}
                dir={dir}
                onSortClick={onSortClick}
              />
              <SortableHeader
                className="text-right w-24"
                label="Profit/mo"
                sortKey="profit"
                activeSort={sort}
                dir={dir}
                onSortClick={onSortClick}
              />
              {showReasonColumn && <th className="px-4 py-2 font-medium w-40">Reason</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {rows.map((row) => (
              <ResearchTableRow
                key={row.candidateId}
                row={row}
                showReasonColumn={showReasonColumn}
                onClick={() => onRowClick(row)}
                lastViewed={lastViewedRowId === row.candidateId}
                onLinkVisit={() => onLinkVisit(row.candidateId)}
                selected={selectedRows.has(row.candidateId)}
                onToggle={() => onToggleRow(row.candidateId)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Combined "Profit/mo" (Faraz 2026-09-27) — margin per unit (cents) x
 *  monthly sales estimate, computed client-side from fields the row
 *  already carries (no new schema field — see research-projection-reader.ts
 *  sortColumns.profit for the equivalent server-side SQL used for
 *  ordering). Null whenever either input is null, same as every other
 *  derived cell on this page. */
function profitPerMonthCents(row: PlanningResearchRow): number | null {
  if (row.estMarginCents === null || row.velocity.estimate === null) return null;
  return row.estMarginCents * row.velocity.estimate;
}

function ResearchTableRow({
  row,
  showReasonColumn,
  onClick,
  lastViewed,
  onLinkVisit,
  selected,
  onToggle,
}: {
  row: PlanningResearchRow;
  showReasonColumn: boolean;
  onClick: () => void;
  lastViewed: boolean;
  onLinkVisit: () => void;
  selected: boolean;
  onToggle: () => void;
}) {
  const label = row.description ?? row.itemNo ?? row.upc ?? row.key;
  const marginClass =
    row.estMarginCents === null ? '' : row.estMarginCents < 0 ? 'text-negative' : 'text-positive';
  const showPackMark = row.pack.suspect || row.pack.confidence !== 'known';
  const packMarkTitle =
    packBadge(row.pack)?.title ?? 'Pack size not confirmed — check units per listing.';
  const velocity = velocityChip(row.velocity.tier);
  const freshness = researchFreshness(row.checkedAt);
  const status = researchStatus(row);

  return (
    <tr
      onClick={onClick}
      onClickCapture={(e) => {
        if ((e.target as HTMLElement).closest('a')) onLinkVisit();
      }}
      className={cn(
        'cursor-pointer hover:bg-surface-elev focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/30',
        selected && !lastViewed && 'bg-surface-elev',
        lastViewed && 'bg-accent/10 hover:bg-accent/10'
      )}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick();
      }}
    >
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`Select ${label}`}
          checked={selected}
          onChange={onToggle}
        />
      </td>
      <td className="px-4 py-2.5">
        <div className="flex gap-3">
          {/* PlanningResearchRow carries no image URL today — see the
           *  implementation report's note; this always renders the
           *  initial-letter fallback tile, same as every other page's
           *  ProductThumb once imageUrl is null. */}
          <ProductThumb imageUrl={null} alt={label} size="sm" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="text-fg-primary line-clamp-2" title={label}>
                {label}
              </p>
              {showPackMark && (
                <Badge variant="warning" title={packMarkTitle}>
                  pack?
                </Badge>
              )}
              {lastViewed && (
                <span className="text-[10px] font-medium text-accent">Last viewed</span>
              )}
            </div>
            {row.amazon.title && (
              <p className="text-[10px] text-fg-tertiary line-clamp-1">
                <ProductLink asin={row.asin} className="hover:underline">
                  {row.amazon.title}
                </ProductLink>
              </p>
            )}
            <p className="text-xs text-fg-secondary font-mono select-text">
              ASIN {row.asin || '—'}
            </p>
            <p className="text-xs text-fg-secondary font-mono select-text">UPC {row.upc || '—'}</p>
            <p className="text-[10px] text-fg-tertiary" aria-label="Research status">
              Amazon {status.amazon} · Keepa {status.keepa} ·{' '}
              {row.checkedAt ? `Checked ${shortDateLabel(row.checkedAt)}` : 'Not checked'}
              {freshness === 'stale' && (
                <Badge variant="warning" className="ml-1">
                  stale
                </Badge>
              )}
            </p>
            {row.gating.status === 'approval_required' && (
              <p className="text-[10px] text-fg-tertiary">
                Needs approval:{' '}
                {approvalNeededSubject(row.gating.gateKind, row.amazon.brand, row.category, label)}
                {row.gating.applyUrl && (
                  <>
                    {' · '}
                    <a
                      href={row.gating.applyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      Request approval
                    </a>
                  </>
                )}
              </p>
            )}
            {row.gating.status === 'unchecked' && row.verdict.bucket === 'buy' && (
              <p className="text-[10px] text-fg-tertiary">Sellability not checked yet</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">{money(row.costCents)}</td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {money(row.anchorPriceCents)}
        {isLowestFbaOfferAnchor(row.anchorSource) && (
          <p className="text-[10px] text-fg-tertiary">lowest FBA</p>
        )}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">{money(row.estFeesCents)}</td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        <p className={marginClass}>{money(row.estMarginCents)}</p>
        <p className="text-[10px] text-fg-tertiary">{percent(row.marginPct)}</p>
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {row.velocity.estimate !== null && <p>{row.velocity.estimate.toLocaleString('en-US')}</p>}
        <Badge variant={velocity.variant}>{velocity.label}</Badge>
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        <p>{moneyWhole(profitPerMonthCents(row))}</p>
      </td>
      {showReasonColumn && (
        <td className="px-4 py-2.5 text-fg-secondary">{researchDecisionReason(row)}</td>
      )}
    </tr>
  );
}

function Pagination({
  offset,
  limit,
  total,
  onPrev,
  onNext,
}: {
  offset: number;
  limit: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + limit, total);
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-fg-secondary">
      <p>
        Showing {start}–{end} of {total}
      </p>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" disabled={offset === 0} onClick={onPrev}>
          Prev
        </Button>
        <Button variant="secondary" size="sm" disabled={end >= total} onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
}
